import React, {useCallback, useEffect, useState} from 'react';
import {Popup} from "devextreme-react/popup";
import {TextBox} from "devextreme-react";
import {CheckBox} from "devextreme-react/check-box";
import {Button} from "devextreme-react/button";
import Guid from "../../models/Guid";
import './carte-credit-popup.css'
import IClient from "../../models/IClient";
import useProvince from "../selectbox/provinces";

export interface ICarteCreditPopupProps {
    client: IClient | undefined
    annulerOnClick?: () => void
    okOnClick?: (infoCarteCredit: IInfoCarteCredit) => void
}

export interface IInfoCarteCredit {
    numero: string,
    expiration: string,
    vcc: string,
    nom: string,
    adresse: string,
    ville: string,
    codePostal: string,
    province: Guid,
    telephone: string,
    courriel: string
    idClient: Guid
}

const CarteCreditPopup = ({client, okOnClick, annulerOnClick}: ICarteCreditPopupProps) => {
    const {ProvinceSelectBox, currentProvince} = useProvince();
    const [infoCarteCredit, setInfoCarteCredit] = useState<IInfoCarteCredit>({
        expiration: "",
        numero: "",
        vcc: "",
        adresse: "",
        codePostal: "",
        courriel: "",
        nom: "",
        ville: "",
        province: Guid.empty,
        telephone: "",
        idClient: client?.idClient || Guid.empty,
    })

    const onPrendreInfoValueChanged = useCallback((e: any) => {
        if (e.value) {
            setInfoCarteCredit(prev => ({
                ...prev, adresse: client?.adresse || '',
                codePostal: client?.codePostal || '',
                courriel: client?.email || '',
                nom: `${client?.prenom} ${client?.nom}`,
                ville: client?.ville || '',
                province: client?.idProvince || Guid.empty,
                telephone: client?.telephone1 || '',
            }))
        } else {
            setInfoCarteCredit(prev => ({
                ...prev, adresse: "",
                codePostal: "",
                courriel: "",
                nom: "",
                ville: "",
                province: Guid.empty,
                telephone: ""
            }))
        }
    }, [client?.adresse, client?.codePostal, client?.email, client?.prenom, client?.nom, client?.ville, client?.idProvince, client?.telephone1]);

    const onNomValueChanged = useCallback((e: any) => {
        setInfoCarteCredit(prev => ({...prev, nom: e.value}))
    }, []);
    const onAdresseValueChanged = useCallback((e: any) => {
        setInfoCarteCredit(prev => ({...prev, adresse: e.value}))
    }, []);
    const onVilleValueChanged = useCallback((e: any) => {
        setInfoCarteCredit(prev => ({...prev, ville: e.value}))
    }, []);
    const onCodePostalValueChanged = useCallback((e: any) => {
        setInfoCarteCredit(prev => ({...prev, codePostal: e.value}))
    }, []);
    const onTelephoneValueChanged = useCallback((e: any) => {
        setInfoCarteCredit(prev => ({...prev, telephone: e.value}))
    }, []);
    const onCourrielValueChanged = useCallback((e: any) => {
        setInfoCarteCredit(prev => ({...prev, courriel: e.value}))
    }, []);
    useEffect(() => {
        setInfoCarteCredit(prev => ({...prev, province: currentProvince?.idProvince || Guid.empty}))
    }, [currentProvince]);
    return (
        <Popup
            onHiding={annulerOnClick}
            visible={true}
            dragEnabled={true}
            resizeEnabled={true}
            hideOnOutsideClick={false}
            showCloseButton={true}
            showTitle={true}
            dragOutsideBoundary={true}
            title="Options de réservation"
            width={"450px"}
            height={"auto"}
        >
            <div id={"popup-carte-credit"} style={{display: "flex", flexDirection: "column", gap: "1rem"}}>
                <span className={"dx-form-group-caption"}>Carte de crédit</span>
                <div style={{display: "flex", flexDirection: "row", gap: "1rem", padding: "0.5rem 0"}}>
                    <div className={"field"} style={{width: "85%"}}>
                        <div>Numéro</div>
                        <TextBox value={infoCarteCredit.numero} maskChar={""} mask={"0000 0000 0000 0009"} onValueChanged={(e) => {
                            setInfoCarteCredit(prev => ({...prev, numero: e.value}))
                        }}/>
                    </div>
                    <div className={"field"}>
                        <div>Exp.</div>
                        <TextBox value={infoCarteCredit.expiration} mask={"00/00"} maskChar={""} maskRules={{
                            0: (char: string, index: number, fullStr: any) => {
                                const year = parseInt(new Date().getFullYear().toString().slice(-2))
                                const month = new Date().getMonth() + 1;
                                if (index === 0) {
                                    return [0, 1].includes(parseInt(char))
                                }

                                if (index === 3) {
                                    return [2, 3, 4, 5, 6, 7, 8, 9].includes(parseInt(char))
                                }
                                if (index === 4) {
                                    return parseInt(fullStr[3] + char) > year || parseInt(fullStr[3] + char) === year && parseInt(fullStr.slice(0, 2)) >= month
                                }

                                return [[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].includes(parseInt(char))]
                            }
                        }} onValueChanged={(e) => {
                            setInfoCarteCredit(prev => ({...prev, expiration: e.value}))
                        }}/>
                    </div>
                    <div className={"field"}>
                        <div>Vcc</div>
                        <TextBox value={infoCarteCredit.vcc} maskChar={""} mask={"0009"} onValueChanged={(e) => {
                            setInfoCarteCredit(prev => ({...prev, vcc: e.value}))
                        }}/>
                    </div>
                </div>
                <span className={"dx-form-group-caption"}>Information du détenteur de la carte</span>
                <div style={{display: "flex", flexDirection: "column", gap: "0.5rem", padding: "0.5rem 0"}}>
                    <div className={"field"}>
                        <div><CheckBox onValueChanged={onPrendreInfoValueChanged}/></div>
                        <div>Prendre les information du dossier du client</div>
                    </div>
                    <div className={"field"}>
                        <div className={"label"}>Nom</div>
                        <div style={{flex: 1}}><TextBox value={infoCarteCredit.nom} onValueChanged={onNomValueChanged}/></div>
                    </div>
                    <div className={"field"}>
                        <div className={"label"}> Adresse</div>
                        <div style={{flex: 1}}>
                            <TextBox value={infoCarteCredit.adresse} onValueChanged={onAdresseValueChanged}/>
                        </div>
                    </div>
                    <div className={"field"}>
                        <div className={"label"}>Ville</div>
                        <div style={{flex: 1}}><TextBox value={infoCarteCredit.ville} onValueChanged={onVilleValueChanged}/></div>
                    </div>
                    <div className={"field"}>
                        <div className={"label"}>Code Postal</div>
                        <div style={{flex: 1}}>
                            <TextBox value={infoCarteCredit.codePostal} onValueChanged={onCodePostalValueChanged}/></div>
                    </div>
                    <div className={"field"}>
                        <div className={"label"}>Province</div>
                        <div style={{flex: 1}}><ProvinceSelectBox value={infoCarteCredit.province}/></div>
                    </div>
                    <div className={"field"}>
                        <div className={"label"}>Téléphone</div>
                        <div style={{flex: 1}}><TextBox value={infoCarteCredit.telephone} onValueChanged={onTelephoneValueChanged}/></div>
                    </div>
                    <div className={"field"}>
                        <div className={"label"}>Courriel</div>
                        <div style={{flex: 1}}><TextBox value={infoCarteCredit.courriel} onValueChanged={onCourrielValueChanged}/></div>
                    </div>
                </div>
                <div style={{display: "flex", gap: "1rem", alignSelf: "end"}}>
                    <Button text={"Ok"} width={"7rem"} onClick={() => okOnClick && okOnClick(infoCarteCredit)}/>
                    <Button width={"7rem"} text={"Annuler"} onClick={annulerOnClick}/>
                </div>
            </div>

        </Popup>
    );
};

export default CarteCreditPopup;